import { CSSProperties, FC, useId } from "react";

// The envelope's paper, drawn in layers so the note can slide between them:
// the back (the inside you see above the pocket), the pocket in front of the
// note, and the top flap with an outside and an inside face. Everything is
// drawn on a 460×300 sheet and shaded with semantic tokens only, so it follows
// the theme.

const WIDTH = 460;
const HEIGHT = 300;
const RADIUS = 12;
export const FLAP_HEIGHT = 176;

const stop = (offset: string, color: string, opacity = 1) => (
  <stop
    offset={offset}
    style={{ stopColor: `var(${color})`, stopOpacity: opacity }}
  />
);

const flood = (color: string): CSSProperties => ({
  floodColor: `var(${color})`,
});

// Gradient and filter ids must be unique per envelope on the page.
const useIds = (...names: string[]) => {
  const base = useId().replace(/:/g, "");
  return Object.fromEntries(names.map((name) => [name, `${base}-${name}`]));
};

const SHEET = `M${RADIUS} 0.5 H${WIDTH - RADIUS} Q${WIDTH - 0.5} 0.5 ${WIDTH - 0.5} ${RADIUS} V${HEIGHT - RADIUS} Q${WIDTH - 0.5} ${HEIGHT - 0.5} ${WIDTH - RADIUS} ${HEIGHT - 0.5} H${RADIUS} Q0.5 ${HEIGHT - 0.5} 0.5 ${HEIGHT - RADIUS} V${RADIUS} Q0.5 0.5 ${RADIUS} 0.5 Z`;
const POCKET_EDGE = `M0.5 6 V${HEIGHT - RADIUS} Q0.5 ${HEIGHT - 0.5} ${RADIUS} ${HEIGHT - 0.5} H${WIDTH - RADIUS} Q${WIDTH - 0.5} ${HEIGHT - 0.5} ${WIDTH - 0.5} ${HEIGHT - RADIUS} V6`;
const LEFT_FLAP = "M0 6 L204 170 Q222 184 204 198 L0 300 Z";
const RIGHT_FLAP = "M460 6 L256 170 Q238 184 256 198 L460 300 Z";
const BOTTOM_FLAP =
  "M0 300 L0 294 L210 152 Q230 139 250 152 L460 294 L460 300 Z";
const TOP_FLAP = `M${RADIUS} 0.5 H${WIDTH - RADIUS} Q${WIDTH - 0.5} 0.5 ${WIDTH - 0.5} ${RADIUS} L250 162 Q230 176 210 162 L0.5 ${RADIUS} Q0.5 0.5 ${RADIUS} 0.5 Z`;

// Faint paper grain, multiplied over a layer.
const Grain: FC<{ id: string }> = ({ id }) => (
  <filter id={id} x="0" y="0" width="100%" height="100%">
    <feTurbulence
      type="fractalNoise"
      baseFrequency="0.85"
      numOctaves="2"
      stitchTiles="stitch"
    />
    <feColorMatrix values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.05 0" />
    <feComposite in2="SourceGraphic" operator="in" />
  </filter>
);

// The inside of the envelope, behind the note: darker toward the top where
// the flap's hinge shades it.
export const EnvelopeBack: FC<{ className?: string }> = ({ className }) => {
  const ids = useIds("inside", "grain");
  return (
    <svg aria-hidden viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className={className}>
      <defs>
        <linearGradient id={ids.inside} x1="0" y1="0" x2="0" y2="1">
          {stop("0", "--color-bg-tertiary")}
          {stop("0.6", "--color-bg-secondary")}
        </linearGradient>
        <Grain id={ids.grain} />
      </defs>
      <path d={SHEET} fill={`url(#${ids.inside})`} />
      <path d={SHEET} filter={`url(#${ids.grain})`} />
      <path
        d={SHEET}
        fill="none"
        style={{ stroke: "var(--color-border-secondary)" }}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

// The front of the envelope, in front of the note: two side flaps under a
// bottom flap, each lit from above and casting a soft shadow on the next.
export const EnvelopePocket: FC<{ className?: string }> = ({ className }) => {
  const ids = useIds("clip", "left", "right", "bottom", "lift", "grain");
  const crease = {
    fill: "none",
    style: { stroke: "var(--color-border-primary)" },
    strokeWidth: 1,
    strokeLinejoin: "round",
    vectorEffect: "non-scaling-stroke",
  } as const;
  return (
    <svg aria-hidden viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className={className}>
      <defs>
        <clipPath id={ids.clip}>
          <path d={SHEET} />
        </clipPath>
        <linearGradient id={ids.left} x1="0" y1="0" x2="1" y2="0">
          {stop("0", "--color-bg-elevated")}
          {stop("1", "--color-bg-secondary")}
        </linearGradient>
        <linearGradient id={ids.right} x1="1" y1="0" x2="0" y2="0">
          {stop("0", "--color-bg-elevated")}
          {stop("1", "--color-bg-secondary")}
        </linearGradient>
        <linearGradient id={ids.bottom} x1="0" y1="0" x2="0" y2="1">
          {stop("0", "--color-bg-elevated")}
          {stop("1", "--color-bg-subtle")}
        </linearGradient>
        <filter id={ids.lift} x="-10%" y="-30%" width="120%" height="160%">
          <feDropShadow
            dx="0"
            dy="-1"
            stdDeviation="2.5"
            style={flood("--color-border-secondary")}
          />
        </filter>
        <Grain id={ids.grain} />
      </defs>
      <g clipPath={`url(#${ids.clip})`}>
        <path
          d={LEFT_FLAP}
          fill={`url(#${ids.left})`}
          filter={`url(#${ids.lift})`}
        />
        <path
          d={RIGHT_FLAP}
          fill={`url(#${ids.right})`}
          filter={`url(#${ids.lift})`}
        />
        <path
          d={BOTTOM_FLAP}
          fill={`url(#${ids.bottom})`}
          filter={`url(#${ids.lift})`}
        />
        <path d={BOTTOM_FLAP} {...crease} />
        <path
          d={`M0 0 H${WIDTH} V${HEIGHT} H0 Z`}
          filter={`url(#${ids.grain})`}
        />
      </g>
      {/* Sides and bottom only: the top edge is the pocket's open mouth. */}
      <path d={POCKET_EDGE} {...crease} />
    </svg>
  );
};

// The top flap. Its outside faces the viewer while sealed; once it swings
// open (rotateX 180°) its inside shows instead.
export const EnvelopeFlap: FC = () => {
  const ids = useIds("outside", "inside", "drop", "grain");
  const face = "absolute inset-0 size-full overflow-visible backface-hidden";
  const edge = {
    fill: "none",
    style: { stroke: "var(--color-border-primary)" },
    strokeWidth: 1,
    strokeLinejoin: "round",
    vectorEffect: "non-scaling-stroke",
  } as const;
  return (
    <>
      <svg aria-hidden viewBox={`0 0 ${WIDTH} ${FLAP_HEIGHT}`} className={face}>
        <defs>
          <linearGradient id={ids.outside} x1="0" y1="0" x2="0" y2="1">
            {stop("0", "--color-bg-subtle")}
            {stop("0.5", "--color-bg-elevated")}
            {stop("1", "--color-bg-elevated")}
          </linearGradient>
          <filter id={ids.drop} x="-10%" y="-10%" width="120%" height="140%">
            <feDropShadow
              dx="0"
              dy="3"
              stdDeviation="4"
              style={flood("--color-border-tertiary")}
            />
          </filter>
          <Grain id={ids.grain} />
        </defs>
        <path
          d={TOP_FLAP}
          fill={`url(#${ids.outside})`}
          filter={`url(#${ids.drop})`}
        />
        <path d={TOP_FLAP} filter={`url(#${ids.grain})`} />
        <path d={TOP_FLAP} {...edge} />
      </svg>
      <svg
        aria-hidden
        viewBox={`0 0 ${WIDTH} ${FLAP_HEIGHT}`}
        className={face}
        style={{ transform: "rotateX(180deg)" }}
      >
        <defs>
          <linearGradient id={ids.inside} x1="0" y1="0" x2="0" y2="1">
            {stop("0", "--color-bg-tertiary")}
            {stop("1", "--color-bg-secondary")}
          </linearGradient>
        </defs>
        {/* Mirrored so that, after both 180° turns, the tip points up. */}
        <g transform={`translate(0 ${FLAP_HEIGHT}) scale(1 -1)`}>
          <path d={TOP_FLAP} fill={`url(#${ids.inside})`} />
          <path d={TOP_FLAP} {...edge} />
        </g>
      </svg>
    </>
  );
};
